import { getBurdenRateLabel } from '~/features/campaign_input/BurdenRateSelector'
import { Stack, Typography, Link, Box, Grid, Chip, Divider, Alert } from '@mui/material'
import { CampaignSectionContainer } from '~/components/common/CampaignSectionContainer'
import { CAMPAIGN_FORM_TITLE, REVIEW_TARGET_LABEL } from '~/constants/campaign'
import { getSponsorServicePriceTitle, getUrlTitle, getMaxCompanionCountTitle } from '~/lib/feature/getCampaignTitle'
import {
  CampaignCategory,
  CampaignRequirementOptions,
  CampaignSnsRequirementSnsPostType,
  CampaignSnsRequirementSnsType,
  Campaign,
  CampaignScheduleType,
  CampaignCostHandlingMethod,
  ExpenseEvidenceType,
  ReviewTarget,
} from '~/generated/graphql'
import { formatPhoneNumber } from '~/lib/phoneNumber'
import { getPrefectureNameJpByPref } from '~/lib/prefectureHelper'
import {
  CampaignAgeLimitLabel,
  CampaignDesiredLanguageLabel,
  CampaignGenreLabel,
  CampaignPrTargetAgeLabel,
  CampaignPrTargetGenderLabel,
  CampaignRequirementCompositionOptionsType,
  CampaignRequirementOptionsLabel,
  CampaignSnsPostTypeListLabel,
  CostHandlingMethodLavel,
  ExpenseEvidenceTypeLabel,
  OptionPricesLabel,
  SubGenreLabel,
} from '~/lib/types/Campaign'
import { CampaignInfoBlock as CIB } from '~/components/common/CampaignInfoBlock'
// 視認性が悪かったので、省略形を使う
import { ImageListType } from 'react-images-uploading'
import { colorPalette } from '~/constants/styles/colors'
import { DateTimeFormat } from '../DateTimeFormat'
import { SnsIcon } from '../SnsIcon'
import { decodeId } from '~/lib/convertId'
import { CampaignReservationWeekdayViewer } from '~/components/common/CampaignReservationWeekdayViewer'
import React, { useContext } from 'react'
import { convertToLineBreaks } from '~/lib/convertToLineBreaks'
import { isWaitingForPublication, isRevisionRequired, isApprovalRequest } from '~/lib/CampaignStatus'
import { DateTimeContext } from '~/contexts/DateTimeContext'
import { formatPostalCode } from '~/lib/postalCode'
import { SUPPORTED_TAG_LANGUAGES, getSnsTagLimit, SNS_TYPES_FOR_DISPLAY, SNS_TYPE_LABELS_FOR_DISPLAY } from '~/lib/sns'

type OnlyCampaignDetailsType = {
  // 表示だけに利用するフィールド
  attention?: string | null | undefined
  useIdentificationTicket?: boolean
  createdAt?: string
  offerEnded?: string | null | undefined
  gross?: number | null | undefined
  status?: Campaign['status']
  // 変換が必要なフィールド
  coverImages?: ImageListType
  campaignRequirementCompositionOptions?: CampaignRequirementCompositionOptionsType
  useRegularlyPublishedCampaign?: boolean
}

type OnlyCampaignCreateType = {
  schedules?: Record<string, boolean>
}

type CampaignContentsProps = Pick<
  Campaign,
  | 'category'
  | 'name'
  | 'companySelectSearchGenre'
  | 'subGenre'
  | 'sponsorServicePrice'
  | 'sponsorServicePriceIntax'
  | 'url'
  | 'prTargetAge'
  | 'prTargetGender'
  | 'ageLimit'
  | 'maxCompanionCount'
  | 'campaignSnsRequirements'
  | 'guidance'
  | 'offerAutoMessage'
  | 'basicGross'
  | 'campaignRequirementOptions'
  | 'campaignRequirementOptionSpecifiedPrText'
  | 'scheduleType'
  | 'costHandlingMethod'
  | 'expenseEvidenceType'
  | 'offerStarted'
  | 'desiredLanguages'
  | 'influencerBurdenRate'
  | 'location'
> &
  OnlyCampaignDetailsType &
  OnlyCampaignCreateType

type Props = {
  campaign: CampaignContentsProps
  isCpConfirmationPage?: boolean
}

export const CampaignContents = ({ campaign, isCpConfirmationPage = false }: Props) => {
  const { getNowJST } = useContext(DateTimeContext)

  const totalOptionPrice = campaign.gross ? campaign.gross - (campaign.basicGross ? campaign.basicGross : 0) : undefined

  if (!campaign.costHandlingMethod) {
    return <Alert severity="error">精算方式が未設定です。</Alert>
  }
  const costHandlingMethod = campaign.costHandlingMethod
  const isExpenseCampaign = costHandlingMethod === CampaignCostHandlingMethod.ExpenseCampaign
  const category = campaign.category
  const location = campaign.location

  const hasOfferStarted = campaign.offerStarted !== undefined

  const shouldDisplayOfferStarted =
    // 作成前
    campaign.status === undefined ||
    // 公開待機中
    (hasOfferStarted && isWaitingForPublication(campaign.status, campaign.offerStarted, getNowJST())) ||
    // 審査中
    isApprovalRequest(campaign.status) ||
    // 差し戻し
    isRevisionRequired(campaign.status)

  return (
    <Stack gap={6}>
      {campaign.category === CampaignCategory.Service && (
        <CampaignSectionContainer title="店舗情報">
          <CIB label="店舗名" isEmpty={!location?.name}>
            <Typography variant="body2">{location?.name}</Typography>
          </CIB>
          <CIB
            label="店舗住所"
            isEmpty={
              !location?.postalCode &&
              !location?.prefectureCode &&
              !location?.cityName &&
              !location?.streetAddress &&
              !location?.buildingName
            }
          >
            <Typography variant="body2">〒{formatPostalCode(location?.postalCode)}</Typography>
            <Typography>
              {[
                location?.prefectureCode ? getPrefectureNameJpByPref(location.prefectureCode) : undefined,
                location?.cityName,
                location?.streetAddress,
                location?.buildingName,
              ]
                .filter(Boolean)
                .join('')}
            </Typography>
          </CIB>
          <CIB label="最寄り駅" isEmpty={!location?.stationName}>
            <Typography variant="body2">{location?.stationName}</Typography>
          </CIB>
          <CIB label="電話番号" isEmpty={!location?.tel}>
            <Typography variant="body2">{location?.tel && formatPhoneNumber(location.tel)}</Typography>
          </CIB>
        </CampaignSectionContainer>
      )}

      <CampaignSectionContainer title="商材情報">
        <CIB label={CAMPAIGN_FORM_TITLE.name} isEmpty={!campaign.name}>
          <Typography variant="body2">{campaign.name}</Typography>
        </CIB>
        <CIB label={CAMPAIGN_FORM_TITLE.selectServiceGenre} isEmpty={!campaign.companySelectSearchGenre}>
          <Typography variant="body2">
            {campaign.companySelectSearchGenre ? CampaignGenreLabel[campaign.companySelectSearchGenre] : null}
          </Typography>
        </CIB>
        <CIB label={REVIEW_TARGET_LABEL[ReviewTarget.SearchSubGenre]} isEmpty={campaign.subGenre.length === 0}>
          <Stack gap={0.5}>
            {campaign.subGenre.map((subGenre) => (
              <Typography key={subGenre} variant="body2">
                {SubGenreLabel[decodeId(subGenre).id as keyof typeof SubGenreLabel]}
              </Typography>
            ))}
          </Stack>
        </CIB>
        <CIB
          label={`${getSponsorServicePriceTitle(costHandlingMethod, category)}${campaign.sponsorServicePriceIntax != null ? '（税込）' : '（税抜）'}`}
          isEmpty={campaign.sponsorServicePriceIntax == null && campaign.sponsorServicePrice == null}
        >
          <Typography>
            ¥
            {campaign.sponsorServicePriceIntax != null
              ? formatPrice(campaign.sponsorServicePriceIntax)
              : formatPrice(campaign.sponsorServicePrice ?? 0)}
          </Typography>
        </CIB>
        <CIB label={getUrlTitle(costHandlingMethod, category)} isEmpty={!campaign.url}>
          <Link
            href={campaign.url ?? ''}
            target="_blank"
            rel="noreferrer"
            sx={{ wordBreak: 'break-all' }}
            underline="hover"
          >
            {campaign.url}
          </Link>
        </CIB>
        {/* キャンペーン画像はCP作成最終画面と詳細でUIを分ける（サイドバーの表示幅を考慮） */}
        {isCpConfirmationPage && (
          <CIB label="キャンペーンのイメージ写真(最大３枚)" isEmpty={!campaign.coverImages}>
            <Grid container spacing={4} alignContent="center" direction="row" alignItems="center">
              {campaign.coverImages?.map((image, i) => (
                <Grid key={i}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: 160,
                      height: 160,
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      component="img"
                      src={image.dataURL}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />

                    {/* 枠線 */}
                    <Box
                      position="absolute"
                      left={0}
                      top={0}
                      bottom={0}
                      right={0}
                      border={1}
                      borderRadius={1}
                      borderColor={colorPalette.primary.main}
                      sx={{ borderWidth: 2 }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CIB>
        )}
        <CIB label={`${CAMPAIGN_FORM_TITLE.prTarget} [年齢]`} isEmpty={!campaign.prTargetAge}>
          <Stack gap={0.5}>
            {campaign.prTargetAge?.map((age) => (
              <Typography key={age} variant="body2">
                {CampaignPrTargetAgeLabel[age]}
              </Typography>
            ))}
          </Stack>
        </CIB>
        <CIB label={`${CAMPAIGN_FORM_TITLE.prTarget} [性別]`} isEmpty={!campaign.prTargetGender}>
          <Typography variant="body2">
            {campaign.prTargetGender ? CampaignPrTargetGenderLabel[campaign.prTargetGender] : null}
          </Typography>
        </CIB>
        <CIB label={getMaxCompanionCountTitle(costHandlingMethod)} isEmpty={campaign.maxCompanionCount == null}>
          <Typography variant="body2">
            {campaign.maxCompanionCount === 0 ? '同伴者なし' : `${campaign.maxCompanionCount}人`}
          </Typography>
        </CIB>
      </CampaignSectionContainer>

      <CampaignSectionContainer title="精算形式">
        <CIB label="精算方法">
          <Typography variant="body2">{CostHandlingMethodLavel[campaign.costHandlingMethod]}</Typography>
        </CIB>
        {isExpenseCampaign ? (
          <>
            <CIB label="証憑形式">
              <Typography variant="body2">
                {campaign.expenseEvidenceType === ExpenseEvidenceType.Receipt
                  ? ExpenseEvidenceTypeLabel.RECEIPT
                  : ExpenseEvidenceTypeLabel.ORDER_NUMBER}
              </Typography>
            </CIB>
            <CIB label="立替費用還元率">
              <Typography variant="body2">{getBurdenRateLabel(campaign.influencerBurdenRate)}</Typography>
            </CIB>
          </>
        ) : null}
      </CampaignSectionContainer>

      <CampaignSectionContainer title="応募条件・案内文">
        <CIB
          label={`${CAMPAIGN_FORM_TITLE.applicationConditions} [ 20歳未満のインフルエンサー ]`}
          isEmpty={!campaign.ageLimit}
        >
          <Typography variant="body2">{campaign.ageLimit ? CampaignAgeLimitLabel[campaign.ageLimit] : null}</Typography>
        </CIB>
        {campaign.category === CampaignCategory.Service && (
          <CIB label={CAMPAIGN_FORM_TITLE.desiredLanguages} isEmpty={campaign.desiredLanguages.length === 0}>
            <Stack gap={0.5}>
              {campaign.desiredLanguages.map((language) => (
                <Typography key={language} variant="body2">
                  {CampaignDesiredLanguageLabel[language]}
                </Typography>
              ))}
            </Stack>
          </CIB>
        )}
        <CIB label={CAMPAIGN_FORM_TITLE.guidance} isEmpty={!campaign.guidance}>
          <Typography variant="body2">{convertToLineBreaks(campaign.guidance ?? '')}</Typography>
        </CIB>
        {campaign.offerAutoMessage && (
          <CIB label={CAMPAIGN_FORM_TITLE.offerAutoMessage} isEmpty={campaign.offerAutoMessage === ''}>
            <Typography variant="body2">{convertToLineBreaks(campaign.offerAutoMessage)}</Typography>
          </CIB>
        )}
      </CampaignSectionContainer>

      <CampaignSectionContainer title="SNS情報">
        {snsTypes.map(({ label, type }) => {
          const snsList = campaign.campaignSnsRequirements.filter((sns) => sns.snsType === type)

          // 対応するSNSが存在しない場合はスキップ
          if (snsList.length === 0) return null

          return (
            <Stack gap={2} key={label}>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <SnsIcon snsType={type} />
                <Typography fontWeight="bold" variant="body1">
                  {label}
                </Typography>
              </Stack>
              <Stack>
                {snsList.map((sns) => {
                  const hasInstagramStories =
                    sns.snsType === CampaignSnsRequirementSnsType.Instagram &&
                    sns.snsPostTypeList.includes(CampaignSnsRequirementSnsPostType.InstagramStories)
                  return (
                    <Stack key={sns.id} gap={3}>
                      <CIB label={CAMPAIGN_FORM_TITLE.requestUrl} isEmpty={!sns.requestUrl}>
                        <Typography variant="body2">{sns.requestUrl}</Typography>
                      </CIB>
                      {sns.snsType === CampaignSnsRequirementSnsType.Instagram && (
                        <CIB label={CAMPAIGN_FORM_TITLE.snsPostTypeRestrictions} isEmpty={!sns.snsPostTypeList}>
                          <Stack gap={0.5}>
                            {sns.snsPostTypeList.map((postType) => (
                              <Typography key={postType} variant="body2">
                                {CampaignSnsPostTypeListLabel[postType]}
                              </Typography>
                            ))}
                          </Stack>
                        </CIB>
                      )}
                      {hasInstagramStories && (
                        <CIB label={CAMPAIGN_FORM_TITLE.snsPostRedirectUrl}>
                          <Link
                            href={sns.snsPostRedirectUrl ?? ''}
                            target="_blank"
                            rel="noreferrer"
                            sx={{ wordBreak: 'break-all' }}
                            underline="hover"
                          >
                            {sns.snsPostRedirectUrl}
                          </Link>
                        </CIB>
                      )}
                      <Stack gap={1}>
                        {(() => {
                          // サポートされている言語のみをフィルタリング
                          const supportedDesiredLanguages = campaign.desiredLanguages.filter((lang) =>
                            SUPPORTED_TAG_LANGUAGES.includes(lang as (typeof SUPPORTED_TAG_LANGUAGES)[number]),
                          )
                          const hasAnyTags = supportedDesiredLanguages.some((lang) =>
                            sns.tagList.some((tl) => tl.languageType === lang && tl.tags.length > 0),
                          )
                          return (
                            <>
                              <CIB label={CAMPAIGN_FORM_TITLE.hashTagSetup} isEmpty={!hasAnyTags}>
                                <Stack gap={2.5}>
                                  {supportedDesiredLanguages.map((languageType) => {
                                    const tagListItem = sns.tagList.find((tl) => tl.languageType === languageType)
                                    const snsTagLimit = getSnsTagLimit(sns.snsType)
                                    const tagCount = tagListItem?.tags.length || 0

                                    return (
                                      <Stack key={languageType} gap={1}>
                                        <Typography variant="body2">
                                          {CampaignDesiredLanguageLabel[languageType]}投稿用のハッシュタグ ({tagCount}/
                                          {snsTagLimit})
                                        </Typography>
                                        {tagCount > 0 ? (
                                          <Stack direction="row" gap={1} flexWrap="wrap">
                                            {tagListItem?.tags.map((tag, index) => (
                                              <Chip
                                                key={index}
                                                label={tag}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ width: 'fit-content' }}
                                              />
                                            ))}
                                          </Stack>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            未設定
                                          </Typography>
                                        )}
                                      </Stack>
                                    )
                                  })}
                                </Stack>
                              </CIB>
                              {hasInstagramStories && (
                                <Typography variant="caption">※{INSTAGRAM_STORY_HASHTAG_TEXT}</Typography>
                              )}
                            </>
                          )
                        })()}
                      </Stack>
                    </Stack>
                  )
                })}
              </Stack>
            </Stack>
          )
        })}
      </CampaignSectionContainer>

      <CampaignSectionContainer title="報酬・オプション設定">
        <CIB label={CAMPAIGN_FORM_TITLE.gross} isEmpty={campaign.gross == null}>
          <Typography variant="body2">¥{(campaign.basicGross ?? 0).toLocaleString()}</Typography>
        </CIB>
        <CIB label="選択したオプション" isEmpty={!campaign.campaignRequirementOptions.length}>
          <Stack gap={2}>
            {campaign.campaignRequirementOptions.map((option, index) => {
              const compositionOptions = [
                CampaignRequirementOptions.CompositionInfluencerAndService,
                CampaignRequirementOptions.CompositionProductOnly,
                CampaignRequirementOptions.CompositionUsageInContext,
              ] as const

              return (
                <Stack key={option} gap={1}>
                  {option === CampaignRequirementOptions.DraftCheck && (
                    <Stack direction="row" gap={0.5} justifyContent="space-between">
                      <Typography variant="body2">{CampaignRequirementOptionsLabel[option]}</Typography>
                      <Typography variant="body2">￥{OptionPricesLabel[option].toLocaleString()}</Typography>
                    </Stack>
                  )}
                  {option === CampaignRequirementOptions.SecondaryUse && (
                    <Stack direction="row" gap={0.5} justifyContent="space-between">
                      <Typography variant="body2">{CampaignRequirementOptionsLabel[option]}</Typography>
                      <Typography variant="body2">￥{OptionPricesLabel[option].toLocaleString()}</Typography>
                    </Stack>
                  )}

                  {/* PRテキストが指定されている場合のみ、指定されたPRテキストとともにオプションラベルを表示 */}
                  {option === CampaignRequirementOptions.SpecifiedPrText && (
                    <Stack gap={1}>
                      <Stack direction="row" gap={0.5} justifyContent="space-between">
                        <Typography variant="body2">{CampaignRequirementOptionsLabel[option]}</Typography>
                        <Typography variant="body2">￥{OptionPricesLabel[option].toLocaleString()}</Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        pl={2}
                        color="text.secondary"
                        sx={{ width: { xs: '100%', sm: '80%' } }}
                      >
                        {campaign.campaignRequirementOptionSpecifiedPrText}
                      </Typography>
                    </Stack>
                  )}

                  {/* 構図指定のオプションの最初の出現時に「構図指定」を表示 */}
                  {compositionOptions.includes(option as (typeof compositionOptions)[number]) &&
                    campaign.campaignRequirementOptions.findIndex((o) =>
                      compositionOptions.includes(o as (typeof compositionOptions)[number]),
                    ) === index && (
                      <Stack gap={1}>
                        <Stack direction="row" gap={0.5} justifyContent="space-between">
                          <Typography variant="body2">構図指定</Typography>
                          <Typography variant="body2">￥{OptionPricesLabel[option].toLocaleString()}</Typography>
                        </Stack>
                        <Typography variant="body2" pl={2} color="text.secondary">
                          {CampaignRequirementOptionsLabel[option]}
                        </Typography>
                      </Stack>
                    )}
                </Stack>
              )
            })}
          </Stack>
          <Divider sx={{ my: 3 }} />
          <Stack direction="row" gap={0.5} justifyContent="space-between">
            <Typography variant="body2">オプションの合計単価（税抜）</Typography>
            <Typography variant="body2">￥{(totalOptionPrice ?? 0).toLocaleString()}</Typography>
          </Stack>
        </CIB>
        {campaign.gross != null && (
          <CIB label="インフルエンサー1人当たりの請求単価（税抜）">
            <Typography variant="body2">¥{(campaign.gross ?? 0).toLocaleString()}</Typography>
          </CIB>
        )}
      </CampaignSectionContainer>

      {isCpConfirmationPage &&
        campaign.scheduleType === CampaignScheduleType.ReservableSchedule &&
        !isExpenseCampaign && (
          <CampaignSectionContainer title="来店日時">
            <CampaignReservationWeekdayViewer schedules={campaign.schedules ?? {}} />
          </CampaignSectionContainer>
        )}

      <CampaignSectionContainer title="公開設定" isLast={isCpConfirmationPage}>
        {shouldDisplayOfferStarted && (
          <CIB label="公開希望日">
            {campaign.offerStarted ? (
              <DateTimeFormat dateTime={campaign.offerStarted} format="yyyy年MM月dd日" />
            ) : (
              <Typography variant="body2">承認後すぐに公開したい</Typography>
            )}
          </CIB>
        )}
        <CIB label="定期公開設定" isEmpty={campaign.useRegularlyPublishedCampaign === undefined}>
          <Typography variant="body2">{campaign.useRegularlyPublishedCampaign ? '有効' : '未設定'}</Typography>
        </CIB>
      </CampaignSectionContainer>

      {!isCpConfirmationPage && (
        <CampaignSectionContainer title="その他の情報" isLast>
          {campaign.useIdentificationTicket !== undefined && (
            <CIB label="本人確認の方法">
              <Typography variant="body2">
                {campaign.useIdentificationTicket ? '本人確認証を利用する' : '本人確認証を利用しない'}
              </Typography>
            </CIB>
          )}
          {campaign.attention && (
            <CIB label={CAMPAIGN_FORM_TITLE.attention}>
              <Typography variant="body2">{convertToLineBreaks(campaign.attention)}</Typography>
            </CIB>
          )}
          {campaign.createdAt && (
            <CIB label="作成日">
              <DateTimeFormat dateTime={campaign.createdAt} format="yyyy年MM月dd日" />
            </CIB>
          )}
          {campaign.offerStarted && (
            <CIB label="公開日">
              <DateTimeFormat dateTime={campaign.offerStarted} format="yyyy年MM月dd日" />
            </CIB>
          )}
          {campaign.offerEnded && (
            <CIB label="募集終了日">
              <DateTimeFormat dateTime={campaign.offerEnded} format="yyyy年MM月dd日" />
            </CIB>
          )}
        </CampaignSectionContainer>
      )}
    </Stack>
  )
}

const formatPrice = (price: number) => price.toLocaleString(undefined, { maximumFractionDigits: 10 })

const snsTypes = SNS_TYPES_FOR_DISPLAY.map((type) => ({
  label: SNS_TYPE_LABELS_FOR_DISPLAY[type],
  type,
}))

export const INSTAGRAM_STORY_HASHTAG_TEXT = 'ストーリーズ投稿では、ハッシュタグは「#PR」のみとなり、他は指定できません'
